import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

// ---------------------------------------------------------------------------
// EDUCATION
// Content lives in `content/education/<slug>/index.md`. Each file's frontmatter
// (degree, school, range, location) plus its markdown body (detail bullets)
// renders as one card. Add a folder to add a degree; newest `date` first.
// ---------------------------------------------------------------------------
const StyledEducationSection = styled.section`
  max-width: 900px;
`;

const StyledEducationCard = styled.div`
  ${({ theme }) => theme.mixins.boxShadow};
  padding: 2rem 2.25rem;
  border-radius: var(--border-radius);
  background-color: var(--light-navy);

  &:not(:last-of-type) {
    margin-bottom: 25px;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }

  .edu-degree {
    margin: 0 0 4px;
    color: var(--lightest-slate-org);
    font-size: var(--fz-xxl);
    font-weight: 600;
  }

  .edu-school {
    margin: 0;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
  }

  .edu-range {
    margin: 4px 0 20px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }

  .edu-details ul {
    ${({ theme }) => theme.mixins.fancyList};
    margin: 0;
    font-size: var(--fz-md);

    strong {
      color: var(--lightest-slate-org);
      font-weight: 600;
    }
  }
`;

const Education = () => {
  const data = useStaticQuery(graphql`
    query {
      education: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/education/" } }
        sort: { frontmatter: { date: DESC } }
      ) {
        edges {
          node {
            frontmatter {
              degree
              school
              range
              location
            }
            html
          }
        }
      }
    }
  `);

  const educationItems = data.education.edges;

  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledEducationSection id="education" ref={revealContainer}>
      <h2 className="numbered-heading">Education</h2>

      {educationItems &&
        educationItems.map(({ node }, i) => {
          const { frontmatter, html } = node;
          const { degree, school, range, location } = frontmatter;

          return (
            <StyledEducationCard key={i}>
              <h3 className="edu-degree">{degree}</h3>
              <p className="edu-school">{school}</p>
              <p className="edu-range">
                {range}
                {location ? ` · ${location}` : ''}
              </p>

              <div
                className="edu-details"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </StyledEducationCard>
          );
        })}
    </StyledEducationSection>
  );
};

export default Education;
